import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import type { Profile } from "../types";
import { profilesTrackingService } from "../../../services";
import { produtosApi, seoProfileApi, type ProdutosDaFicha, type SeoResearch } from "../../../services/seo";
import { Aside, AsideCard, Badge, Botao, BotaoPequeno, Coluna, EditorGrid, Hint, Lab, Tip } from "./EditorChrome";
import { StockImageSuggestions } from "./StockImageSuggestions";
import { avisoDaFoto, prepararFotoParaGoogle } from "./foto-para-google";
import { BOTOES_DO_PRODUTO, linkDoWhatsApp, linkInvalido, normalizarLink } from "./seo-produto-link";
import { mergeGabaritoComPublicacao, readGabaritoState, writeGabaritoState } from "./gabarito-utils";
import {
  LIMITE_DESCRICAO,
  LIMITE_NOME,
  MAX_POR_ENVIO,
  type Rascunho,
  gravarRascunhos,
  lerRascunhos,
  motivoParaNaoPublicar,
  publicandoAgora,
  rotuloDaTarefa,
  situacaoNaFicha,
  sugestoesDeProdutos,
  tarefaAberta,
} from "./produtos-da-ficha";

/*
 * Tela Produtos (mesmo desenho de Serviços). Produtos não têm API no Google: quem publica,
 * lê e apaga é o robô da VPS (Chrome logado com a conta do robô, fila seo-ext, operador "robo").
 */

const OPERADOR_DO_ROBO = "robo";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  animation: ${fadeIn} 0.3s ease;
  min-width: 0;
`;

const Titulo = styled.h3`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 22px;
  line-height: 28px;
  font-weight: 500;
  color: #1f1f1f;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Subtitulo = styled.h4`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 600;
  color: #1f1f1f;
`;

const Tabela = styled.div`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
`;

const CabecalhoDaTabela = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 60px;
  gap: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid var(--line);
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  color: #5f6368;
  @media (max-width: 599.95px) {
    display: none;
  }
`;

const Linha = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 60px;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid #eef1f4;
  background: #fff;
  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    background: #eaf1fb;
  }
  .nome {
    font-size: 14px;
    font-weight: 500;
    color: #1f1f1f;
    overflow-wrap: anywhere;
  }
  @media (max-width: 599.95px) {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px 12px;
    & > :first-child {
      grid-column: 1 / -1;
    }
  }
`;

const IconeBotao = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: #5f6368;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: #f3f6fa;
    color: #1f1f1f;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .material-icons {
    font-size: 20px;
  }
`;

const Faixa = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line);
  background: #fff;
`;

const Vazio = styled.div`
  text-align: center;
  padding: 36px 20px;
  color: #5f6368;
  font-size: 13px;
  .material-icons {
    font-size: 40px;
    color: #bdc1c6;
    display: block;
    margin-bottom: 8px;
  }
`;

const Girando = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid #c2d7f5;
  border-top-color: #1264c7;
  border-radius: 50%;
  display: inline-block;
  animation: ${spin} 0.7s linear infinite;
`;

const Cartao = styled.div`
  background: #fff;
  border: 1px solid #c2d7f5;
  border-radius: 16px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${fadeIn} 0.25s ease;
`;

const Grade = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 16px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Foto = styled.div<{ $falta?: boolean }>`
  width: 160px;
  height: 160px;
  border-radius: 12px;
  border: 1px ${(p) => (p.$falta ? "dashed #E37400" : "solid var(--line)")};
  background: #f7f9fc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #5f6368;
  font-size: 12px;
  text-align: center;
  padding: 8px;
  box-sizing: border-box;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Campos = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const Rotulo = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #3c4043;
  text-transform: uppercase;
  .conta {
    text-transform: none;
    font-weight: 400;
    color: #5f6368;
    align-self: flex-end;
    margin-top: -4px;
  }
`;

const campoBase = css`
  width: 100%;
  border: 1px solid #dadce0;
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  color: #1f1f1f;
  box-sizing: border-box;
  text-transform: none;
  letter-spacing: normal;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 1px #1264c7;
  }
  &[aria-invalid="true"] {
    border-color: #b3261e;
  }
`;

const Input = styled.input`
  ${campoBase};
  height: 44px;
  padding: 0 14px;
`;

const TextArea = styled.textarea`
  ${campoBase};
  min-height: 84px;
  padding: 10px 14px;
  line-height: 22px;
  resize: vertical;
`;

const Select = styled.select`
  ${campoBase};
  height: 44px;
  padding: 0 12px;
  background: #fff;
`;

const LinhaDoLink = styled.div`
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr);
  gap: 10px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Atalho = styled.button`
  border: 1px solid #dadce0;
  background: #fff;
  border-radius: 999px;
  height: 28px;
  padding: 0 12px;
  font-size: 12px;
  color: #1264c7;
  cursor: pointer;
  margin-right: 6px;
`;

const BotaoArquivo = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #dadce0;
  font-size: 13px;
  font-weight: 500;
  color: #1264c7;
  cursor: pointer;
  background: #fff;
  .material-icons {
    font-size: 18px;
  }
  input {
    display: none;
  }
`;

const BarraDePublicar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid var(--line);
  label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #3c4043;
    cursor: pointer;
  }
`;

const Aviso = styled.div<{ $tipo: "error" | "success" | "warning" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13px;
  animation: ${fadeIn} 0.25s ease;
  ${({ $tipo }) =>
    $tipo === "error"
      ? css`
          background: #fce8e6;
          color: #b3261e;
        `
      : $tipo === "warning"
        ? css`
            background: #fef7e0;
            color: #8a5a00;
          `
        : css`
            background: #eaf7ee;
            color: #137333;
          `}
  .material-icons {
    font-size: 18px;
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(31, 35, 41, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

const ConfirmBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: 440px;
  max-width: 92vw;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 16px 48px rgba(31, 35, 41, 0.2);
  h4 {
    margin: 0;
    font-family: var(--font-sans);
    font-size: 18px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #b3261e;
  }
  p {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: #3c4043;
  }
`;

const ItemDaAtividade = styled.div<{ $erro?: boolean }>`
  font-size: 12px;
  line-height: 17px;
  padding: 8px 10px;
  border-radius: 10px;
  background: ${(p) => (p.$erro ? "#FCE8E6" : "#F7F9FC")};
  color: ${(p) => (p.$erro ? "#B3261E" : "#3C4043")};
  overflow-wrap: anywhere;
`;

type Feedback = { tipo: "error" | "success" | "warning"; texto: string } | null;

const mensagemDeErro = (erro: unknown) => {
  const dados = (erro as { response?: { data?: { message?: string; userMessage?: string } } })?.response?.data;
  return dados?.userMessage || dados?.message || (erro instanceof Error ? erro.message : "Erro inesperado.");
};

const quando = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const novoId = () => `prod-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

type Props = { profile: Profile | null };

export const ProductsTab: React.FC<Props> = ({ profile }) => {
  const perfilId = profile?.id ?? "";
  const [ficha, setFicha] = useState<ProdutosDaFicha | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [rascunhos, setRascunhos] = useState<Rascunho[]>([]);
  const [pesquisa, setPesquisa] = useState<SeoResearch | null>(null);
  const [pesquisando, setPesquisando] = useState(false);
  const [simular, setSimular] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [apagar, setApagar] = useState<{ nomes: string[]; todos: boolean } | null>(null);
  const leituraPedida = useRef(false);

  // Rascunhos e pesquisa do perfil aberto.
  useEffect(() => {
    if (!perfilId) return;
    setRascunhos(lerRascunhos(perfilId));
    setPesquisa(null);
    leituraPedida.current = false;
    seoProfileApi.getLatestResearch(perfilId).then(setPesquisa).catch(() => setPesquisa(null));
  }, [perfilId]);

  useEffect(() => {
    if (perfilId) gravarRascunhos(perfilId, rascunhos);
  }, [perfilId, rascunhos]);

  const carregar = useCallback(async () => {
    if (!perfilId) return;
    try {
      const atual = await produtosApi.situacao(perfilId);
      setFicha(atual);
      // Primeira vez nesta ficha: pede ao robô para ler o que já está no Google. Se a última
      // leitura falhou (ex.: a conta do robô não gerencia a ficha), não insiste sozinho.
      const leituraFalhou = atual.tarefas.some((t) => t.tipo === "product_list" && t.status === "ERROR");
      if (atual.naFicha === null && !atual.lendo && !leituraFalhou && !leituraPedida.current) {
        leituraPedida.current = true;
        const lida = await produtosApi.ler(perfilId);
        setFicha(lida);
        if (lida.aviso) setFeedback({ tipo: "warning", texto: lida.aviso });
      }
    } catch (erro) {
      setFeedback({ tipo: "error", texto: mensagemDeErro(erro) });
    } finally {
      setCarregando(false);
    }
  }, [perfilId]);

  useEffect(() => {
    setCarregando(true);
    setFicha(null);
    carregar();
  }, [carregar]);

  // Enquanto o robô tem tarefa aberta, acompanha a cada 5 s.
  const roboTrabalhando = !!ficha && (ficha.lendo || ficha.tarefas.some(tarefaAberta));
  useEffect(() => {
    if (!roboTrabalhando) return;
    const t = window.setInterval(carregar, 5000);
    return () => window.clearInterval(t);
  }, [roboTrabalhando, carregar]);

  const naFicha = ficha?.naFicha ?? [];
  const tarefas = ficha?.tarefas ?? [];
  const publicando = useMemo(() => publicandoAgora(tarefas, ficha?.naFicha ?? null), [tarefas, ficha?.naFicha]);
  const sugestoes = useMemo(
    () => sugestoesDeProdutos(pesquisa, [...naFicha, ...publicando, ...rascunhos.map((r) => r.nome)]),
    [pesquisa, naFicha, publicando, rascunhos],
  );
  const motivo = motivoParaNaoPublicar(rascunhos);

  const atualizar = (id: string, mudanca: Partial<Rascunho>) =>
    setRascunhos((atual) => atual.map((r) => (r.id === id ? { ...r, ...mudanca } : r)));

  const adicionar = (nome = "", descricao = "") => {
    setRascunhos((atual) => [...atual, { id: novoId(), nome: nome.slice(0, LIMITE_NOME), descricao: descricao.slice(0, LIMITE_DESCRICAO) }]);
  };

  const fotoDoComputador = (id: string, arquivo?: File) => {
    if (!arquivo) return;
    if (!arquivo.type.startsWith("image/")) {
      setFeedback({ tipo: "error", texto: "Selecione um arquivo de imagem válido." });
      return;
    }
    // O Google não aceita WebP nem foto pequena: converte/amplia antes de guardar.
    prepararFotoParaGoogle(arquivo)
      .then((foto) => {
        atualizar(id, { image: { name: foto.nome, contentType: foto.contentType, dataUrl: foto.dataUrl } });
        const aviso = avisoDaFoto(foto);
        if (aviso) setFeedback({ tipo: "success", texto: aviso });
      })
      .catch((erro: unknown) => setFeedback({ tipo: "error", texto: erro instanceof Error ? erro.message : "Não foi possível ler a imagem." }));
  };

  const pesquisarComIa = async () => {
    if (!perfilId) return;
    setPesquisando(true);
    setFeedback(null);
    try {
      setPesquisa(await seoProfileApi.research(perfilId, ""));
    } catch (erro) {
      setFeedback({ tipo: "error", texto: mensagemDeErro(erro) });
    } finally {
      setPesquisando(false);
    }
  };

  const lerDeNovo = async () => {
    try {
      const lida = await produtosApi.ler(perfilId);
      setFicha(lida);
      if (lida.aviso) setFeedback({ tipo: "warning", texto: lida.aviso });
    } catch (erro) {
      setFeedback({ tipo: "error", texto: mensagemDeErro(erro) });
    }
  };

  const confirmarApagar = async () => {
    if (!apagar) return;
    try {
      setFicha(await produtosApi.apagar(perfilId, apagar));
      setFeedback({
        tipo: "success",
        texto: apagar.todos
          ? "O robô vai apagar todos os produtos da ficha. Leva alguns minutos."
          : `O robô vai apagar ${apagar.nomes.length === 1 ? `"${apagar.nomes[0]}"` : `${apagar.nomes.length} produtos`}.`,
      });
    } catch (erro) {
      setFeedback({ tipo: "error", texto: mensagemDeErro(erro) });
    } finally {
      setApagar(null);
    }
  };

  const publicar = async () => {
    if (motivo || !profile) return;
    setEnviando(true);
    setFeedback(null);
    const enviados = rascunhos;
    try {
      await seoProfileApi.publish({
        profileId: perfilId,
        mode: "products",
        dryRun: simular,
        operatorId: OPERADOR_DO_ROBO,
        items: enviados.map((r) => {
          const buttonUrl = normalizarLink(r.link) ?? undefined;
          return {
            name: r.nome.trim(),
            description: r.descricao,
            requiresConfirmation: false,
            confirmed: true,
            imageName: r.image?.name,
            imageContentType: r.image?.contentType,
            imageDataUrl: r.image?.dataUrl,
            buttonType: buttonUrl ? r.botao || "SAIBA_MAIS" : undefined,
            buttonUrl,
          };
        }),
      });
      setFeedback({
        tipo: "success",
        texto: simular
          ? `Simulação enviada: o robô preenche ${enviados.length} produto(s) sem publicar.`
          : `${enviados.length} produto(s) enviados ao robô. Cada um leva cerca de 1 minuto; a lista se atualiza sozinha.`,
      });
      if (!simular) {
        setRascunhos([]);
        // Como antes no SEO: o que foi publicado entra nas palavras do Gabarito.
        try {
          const remoto = await profilesTrackingService.getGabarito(perfilId).catch(() => null);
          const atualizado = mergeGabaritoComPublicacao(remoto, readGabaritoState(perfilId), enviados.map((r) => r.nome.trim()));
          writeGabaritoState(perfilId, atualizado);
          await profilesTrackingService.saveGabaritoText(perfilId, {
            postKeywords: atualizado.postKeywords,
            commentKeywords: atualizado.commentKeywords,
            postNotes: atualizado.postNotes,
            commentNotes: atualizado.commentNotes,
          });
        } catch {
          setFeedback({ tipo: "warning", texto: "Produtos enviados, mas não consegui gravar as palavras no Gabarito — confira a aba Gabarito." });
        }
      }
      await carregar();
    } catch (erro) {
      setFeedback({ tipo: "error", texto: mensagemDeErro(erro) });
    } finally {
      setEnviando(false);
    }
  };

  if (!profile) return null;

  const linhas = [
    ...naFicha.map((nome) => ({ nome, situacao: situacaoNaFicha(nome, tarefas) as "apagando" | "na_ficha" | "publicando" })),
    ...publicando.map((nome) => ({ nome, situacao: "publicando" as const })),
  ];
  const ultimaLeitura = tarefas.find((t) => t.tipo === "product_list" && !tarefaAberta(t));
  const leituraTexto = ficha?.lendo
    ? "O robô está lendo a ficha no Google (cerca de 1 minuto)."
    : ultimaLeitura?.status === "ERROR"
      ? `O robô não conseguiu ler a ficha: ${ultimaLeitura.mensagem || "erro sem detalhe"}.`
      : ficha?.lidoEm
        ? `Lido pelo robô em ${quando(ficha.lidoEm)}.`
        : "A ficha ainda não foi lida pelo robô.";
  const site = normalizarLink(profile.website);
  const whatsapp = linkDoWhatsApp(profile.whatsapp || profile.phone);

  return (
    <Container>
      <EditorGrid style={{ gridTemplateColumns: "minmax(0, 8fr) minmax(0, 4fr)" }}>
        <Coluna style={{ gap: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Titulo>Produtos</Titulo>
              <Hint>{`Lista que aparece na aba "Produtos" da ficha. O robô da GeoRanking publica e apaga pelo navegador. ${naFicha.length} na ficha.`}</Hint>
            </div>
            <Botao type="button" $variante="filled" onClick={() => adicionar()}>
              <span className="material-icons">add</span>
              Adicionar produto
            </Botao>
          </div>

          {feedback && (
            <Aviso $tipo={feedback.tipo} role={feedback.tipo === "error" ? "alert" : "status"}>
              <span className="material-icons">{feedback.tipo === "error" ? "error_outline" : feedback.tipo === "warning" ? "warning" : "check_circle"}</span>
              <span>{feedback.texto}</span>
            </Aviso>
          )}

          {rascunhos.length > 0 && (
            <Coluna style={{ gap: 12 }}>
              <Subtitulo>{`Para publicar · ${rascunhos.length}`}</Subtitulo>
              {rascunhos.map((r) => (
                <Cartao key={r.id}>
                  <Grade>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <Foto $falta={!r.image}>{r.image ? <img src={r.image.dataUrl} alt={`Foto de ${r.nome || "produto"}`} /> : "Foto obrigatória"}</Foto>
                      <BotaoArquivo>
                        <span className="material-icons">upload</span>
                        Do computador
                        <input
                          type="file"
                          accept="image/*"
                          aria-label={`Foto do computador para ${r.nome || "produto"}`}
                          onChange={(e) => fotoDoComputador(r.id, e.target.files?.[0])}
                        />
                      </BotaoArquivo>
                    </div>
                    <Campos>
                      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <Rotulo style={{ flex: 1 }}>
                          Nome do produto *
                          <Input value={r.nome} maxLength={LIMITE_NOME} placeholder="Ex: Placa Mercosul para carro" onChange={(e) => atualizar(r.id, { nome: e.target.value })} />
                          <span className="conta">{`${r.nome.length}/${LIMITE_NOME}`}</span>
                        </Rotulo>
                        <IconeBotao type="button" title="Tirar da lista" aria-label={`Tirar ${r.nome || "produto"} da lista`} onClick={() => setRascunhos((atual) => atual.filter((x) => x.id !== r.id))} style={{ marginTop: 22 }}>
                          <span className="material-icons">close</span>
                        </IconeBotao>
                      </div>
                      <Rotulo>
                        Descrição
                        <TextArea value={r.descricao} maxLength={LIMITE_DESCRICAO} placeholder="O que é, para quem, diferenciais..." onChange={(e) => atualizar(r.id, { descricao: e.target.value })} />
                      </Rotulo>
                      <LinhaDoLink>
                        <Rotulo>
                          Botão
                          <Select value={r.botao || "SAIBA_MAIS"} onChange={(e) => atualizar(r.id, { botao: e.target.value })}>
                            {BOTOES_DO_PRODUTO.map((b) => (
                              <option key={b.valor} value={b.valor}>
                                {b.rotulo}
                              </option>
                            ))}
                          </Select>
                        </Rotulo>
                        <Rotulo>
                          Link do botão (opcional)
                          <Input
                            inputMode="url"
                            placeholder="https://… site, página ou WhatsApp"
                            value={r.link || ""}
                            aria-invalid={linkInvalido(r.link)}
                            onChange={(e) => atualizar(r.id, { link: e.target.value })}
                          />
                        </Rotulo>
                      </LinhaDoLink>
                      {(site || whatsapp) && (
                        <div>
                          {site && (
                            <Atalho type="button" onClick={() => atualizar(r.id, { link: site })}>
                              Site do perfil
                            </Atalho>
                          )}
                          {whatsapp && (
                            <Atalho type="button" onClick={() => atualizar(r.id, { link: whatsapp })}>
                              WhatsApp
                            </Atalho>
                          )}
                        </div>
                      )}
                    </Campos>
                  </Grade>
                  {r.nome.trim().length >= 3 && (
                    <StockImageSuggestions keyword={r.nome} city={profile.city} onPick={(image) => atualizar(r.id, { image })} />
                  )}
                </Cartao>
              ))}

              <BarraDePublicar>
                <label>
                  <input type="checkbox" checked={simular} onChange={(e) => setSimular(e.target.checked)} aria-label="Simular" />
                  Simular (o robô preenche e não publica)
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  {motivo && <Hint style={{ color: "#8A5A00" }}>{motivo}</Hint>}
                  <Botao type="button" $variante="filled" disabled={!!motivo || enviando} onClick={publicar}>
                    <span className="material-icons">{simular ? "science" : "publish"}</span>
                    {enviando ? "Enviando..." : `${simular ? "Simular" : "Publicar"} ${rascunhos.length} produto${rascunhos.length === 1 ? "" : "s"}`}
                  </Botao>
                </div>
              </BarraDePublicar>
            </Coluna>
          )}

          <Coluna style={{ gap: 12 }}>
            <Subtitulo>Na ficha do Google</Subtitulo>
            <Tabela>
              <Faixa>
                <Hint style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {ficha?.lendo && <Girando aria-hidden />}
                  {carregando ? "Carregando..." : leituraTexto}
                </Hint>
                <div style={{ display: "flex", gap: 8 }}>
                  <BotaoPequeno type="button" $variante="outlined" onClick={lerDeNovo} disabled={!!ficha?.lendo || carregando}>
                    <span className="material-icons">refresh</span>
                    Atualizar lista
                  </BotaoPequeno>
                  <BotaoPequeno
                    type="button"
                    $variante="text"
                    style={{ color: "#B3261E" }}
                    disabled={!naFicha.length}
                    onClick={() => setApagar({ nomes: [], todos: true })}
                  >
                    <span className="material-icons">delete_sweep</span>
                    Apagar todos
                  </BotaoPequeno>
                </div>
              </Faixa>
              <CabecalhoDaTabela>
                <span>Produto</span>
                <span>Situação</span>
                <span />
              </CabecalhoDaTabela>
              {linhas.length === 0 && !carregando && (
                <Vazio>
                  <span className="material-icons">inventory_2</span>
                  {ficha?.naFicha === null ? "Aguardando a primeira leitura do robô." : "Nenhum produto na ficha."}
                </Vazio>
              )}
              {linhas.map((l) => (
                <Linha key={`${l.situacao}-${l.nome}`}>
                  <span className="nome">{l.nome}</span>
                  <Badge $tom={l.situacao === "na_ficha" ? "verde" : l.situacao === "publicando" ? "azul" : "amarelo"}>
                    {l.situacao === "na_ficha" ? "Na ficha" : l.situacao === "publicando" ? "Publicando" : "Apagando"}
                  </Badge>
                  <div>
                    {l.situacao === "na_ficha" && (
                      <IconeBotao type="button" title="Apagar do Google" aria-label={`Apagar ${l.nome}`} onClick={() => setApagar({ nomes: [l.nome], todos: false })}>
                        <span className="material-icons">delete</span>
                      </IconeBotao>
                    )}
                  </div>
                </Linha>
              ))}
            </Tabela>
          </Coluna>
        </Coluna>

        <Aside>
          <AsideCard>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Lab>
                <span className="material-icons" style={{ color: "#1264C7" }}>auto_awesome</span>
                Sugestões GeoRanking
              </Lab>
              <Badge>{sugestoes.length}</Badge>
            </div>
            <Hint>
              {profile.category
                ? `Produtos para "${profile.category}" sugeridos pela IA a partir do Gabarito, do site e da ficha.`
                : "Produtos sugeridos pela IA a partir do Gabarito, do site e da ficha."}
            </Hint>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sugestoes.slice(0, 8).map((s) => (
                <div key={s.nome} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 10, background: "#F7F9FC" }}>
                  <span style={{ fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.nome}>
                    {s.nome}
                  </span>
                  <BotaoPequeno type="button" $variante="tonal" style={{ height: 30 }} onClick={() => adicionar(s.nome, s.descricao)}>
                    Adicionar
                  </BotaoPequeno>
                </div>
              ))}
              {sugestoes.length === 0 && <Hint>{pesquisa ? "Todas as sugestões já foram usadas." : "Ainda não há pesquisa com IA para este perfil."}</Hint>}
            </div>
            <BotaoPequeno type="button" $variante="outlined" onClick={pesquisarComIa} disabled={pesquisando}>
              <span className="material-icons">travel_explore</span>
              {pesquisando ? "Pesquisando..." : pesquisa ? "Pesquisar de novo com IA" : "Pesquisar com IA"}
            </BotaoPequeno>
          </AsideCard>

          {tarefas.length > 0 && (
            <AsideCard>
              <Lab>
                <span className="material-icons" style={{ color: "#1264C7" }}>smart_toy</span>
                Atividade do robô
              </Lab>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {tarefas.slice(0, 8).map((t) => (
                  <ItemDaAtividade key={t.id} $erro={t.status === "ERROR"}>
                    {rotuloDaTarefa(t)}
                    {t.status === "ERROR" && t.mensagem ? ` — ${t.mensagem}` : ""}
                    <div style={{ color: "#5F6368" }}>{quando(t.terminadoEm || t.criadoEm)}</div>
                  </ItemDaAtividade>
                ))}
              </div>
            </AsideCard>
          )}

          <Tip>
            <span className="material-icons">info</span>
            {`O Google exige foto em todo produto. Até ${MAX_POR_ENVIO} por envio. O robô usa a conta georankingbr: ela precisa ser gerente desta ficha.`}
          </Tip>
        </Aside>
      </EditorGrid>

      {apagar && (
        <ConfirmOverlay onClick={() => setApagar(null)}>
          <ConfirmBox role="dialog" aria-label="Apagar produtos" onClick={(e) => e.stopPropagation()}>
            <h4>
              <span className="material-icons">warning</span>
              {apagar.todos ? "Apagar todos os produtos" : "Apagar produto"}
            </h4>
            <p>
              {apagar.todos ? (
                <>
                  O robô vai apagar <b>todos os {naFicha.length} produtos</b> desta ficha no Google. Não dá para desfazer.
                </>
              ) : (
                <>
                  O robô vai apagar <b>"{apagar.nomes[0]}"</b> da ficha no Google. Não dá para desfazer.
                </>
              )}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
              <BotaoPequeno type="button" $variante="text" onClick={() => setApagar(null)}>
                Cancelar
              </BotaoPequeno>
              <BotaoPequeno type="button" $variante="outlined" style={{ color: "#B3261E", borderColor: "#F9DEDC" }} onClick={confirmarApagar}>
                <span className="material-icons">delete</span>
                Apagar
              </BotaoPequeno>
            </div>
          </ConfirmBox>
        </ConfirmOverlay>
      )}
    </Container>
  );
};

export default ProductsTab;
